
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useThreads } from "./threads/useThreads";
import ThreadCategories from "./threads/ThreadCategories";
import ThreadsList from "./threads/ThreadsList";
import EmptyThreads from "./threads/EmptyThreads";
import ThreadsLoading from "./threads/ThreadsLoading";
import CreateThreadDialog from "./threads/CreateThreadDialog";

interface ClubDiscussionThreadsProps {
  clubId: string;
}

const ClubDiscussionThreads: React.FC<ClubDiscussionThreadsProps> = ({ clubId }) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("General");
  const [submitting, setSubmitting] = useState(false);
  
  const { user } = useAuth();
  const { toast } = useToast();
  const { 
    threads, 
    loading, 
    selectedCategory, 
    setSelectedCategory, 
    fetchThreads, 
    handleLikeThread 
  } = useThreads(clubId);

  const handleCreateThread = async () => {
    if (!title.trim() || !content.trim() || !user) {
      toast({
        title: "Missing information",
        description: "Please provide a title and content for your thread",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      const { data, error } = await supabase
        .from("club_threads")
        .insert({
          title,
          content,
          category,
          club_id: clubId,
          user_id: user.id
        })
        .select()
        .single();
        
      if (error) throw error;
      
      toast({
        title: "Thread created",
        description: "Your discussion thread has been posted"
      });
      
      setTitle("");
      setContent("");
      setCategory("General");
      setCreateDialogOpen(false);
      
      // Refresh the threads list
      fetchThreads();
      
    } catch (error) {
      console.error("Error creating thread:", error);
      toast({
        title: "Error",
        description: "Failed to create thread",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Discussion Threads</h3>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus size={16} className="mr-2" /> New Thread
        </Button>
      </div>
      
      <ThreadCategories 
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />
      
      {loading ? (
        <ThreadsLoading />
      ) : threads.length > 0 ? (
        <ThreadsList 
          threads={threads}
          onLikeThread={handleLikeThread}
        />
      ) : (
        <EmptyThreads onCreateThread={() => setCreateDialogOpen(true)} />
      )}
      
      <CreateThreadDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreateThread={handleCreateThread}
        title={title}
        setTitle={setTitle}
        content={content}
        setContent={setContent}
        category={category}
        setCategory={setCategory}
        submitting={submitting}
      />
    </div>
  );
};

export default ClubDiscussionThreads;
